import React, { useEffect, useRef } from 'react';

// --- Types ---
type GradientColors = [string, string, string, string];

interface GradientBackgroundProps {
  colors?: GradientColors;
  className?: string;
}

// --- Shader & WebGL Logic (MiniGL) ---
// Kept outside component to prevent re-declaration on renders
const VERTEX_SHADER = `
varying vec3 v_color;
void main() {
  float time = u_time * u_global.noiseSpeed;
  vec2 noiseCoord = resolution * uvNorm * u_global.noiseFreq;
  vec2 st = 1. - uvNorm.xy;
  float tilt = resolution.y / 2.0 * uvNorm.y;
  float incline = resolution.x * uvNorm.x / 2.0 * u_vertDeform.incline;
  float offset = resolution.x / 2.0 * u_vertDeform.incline * mix(u_vertDeform.offsetBottom, u_vertDeform.offsetTop, uv.y);
  float noise = snoise(vec3(
    noiseCoord.x * u_vertDeform.noiseFreq.x + time * u_vertDeform.noiseFlow,
    noiseCoord.y * u_vertDeform.noiseFreq.y,
    time * u_vertDeform.noiseSpeed + u_vertDeform.noiseSeed
  )) * u_vertDeform.noiseAmp;
  noise *= 1.0 - pow(abs(uvNorm.y), 2.0);
  noise = max(0.0, noise);
  vec3 pos = vec3(
    position.x,
    position.y + tilt + incline + noise - offset,
    position.z
  );
  if (u_active_colors[0] == 1.) {
    v_color = u_baseColor;
  }
  for (int i = 0; i < u_waveLayers_length; i++) {
    if (u_active_colors[i + 1] == 1.) {
      WaveLayers layer = u_waveLayers[i];
      float noise = smoothstep(
        layer.noiseFloor,
        layer.noiseCeil,
        snoise(vec3(
          noiseCoord.x * layer.noiseFreq.x + time * layer.noiseFlow,
          noiseCoord.y * layer.noiseFreq.y,
          time * layer.noiseSpeed + layer.noiseSeed
        )) / 2.0 + 0.5
      );
      v_color = blendNormal(v_color, layer.color, pow(noise, 4.));
    }
  }
  gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
}`;

const FRAGMENT_SHADER = `
varying vec3 v_color;
void main() {
  vec3 color = v_color;
  if (u_darken_top == 1.0) {
    vec2 st = gl_FragCoord.xy/resolution.xy;
    color.g -= pow(st.y + sin(-12.0) * st.x, u_shadow_power) * 0.4;
  }
  gl_FragColor = vec4(color, 1.0);
}`;

const NOISE_SHADER = `
vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 permute(vec4 x) { return mod289(((x*34.0)+1.0)*x); }
vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }
float snoise(vec3 v) {
  const vec2  C = vec2(1.0/6.0, 1.0/3.0) ;
  const vec4  D = vec4(0.0, 0.5, 1.0, 2.0);
  vec3 i  = floor(v + dot(v, C.yyy) );
  vec3 x0 =  v - i + dot(i, C.xxx) ;
  vec3 g = step(x0.yzx, x0.xyz);
  vec3 l = 1.0 - g;
  vec3 i1 = min( g.xyz, l.zxy );
  vec3 i2 = max( g.xyz, l.zxy );
  vec3 x1 = x0 - i1 + C.xxx;
  vec3 x2 = x0 - i2 + C.yyy;
  vec3 x3 = x0 - D.yyy;
  i = mod289(i);
  vec4 p = permute( permute( permute(
             i.z + vec4(0.0, i1.z, i2.z, 1.0 ))
           + i.y + vec4(0.0, i1.y, i2.y, 1.0 ))
           + i.x + vec4(0.0, i1.x, i2.x, 1.0 ));
  float n_ = 0.142857142857;
  vec3  ns = n_ * D.wyz - D.xzx;
  vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
  vec4 x_ = floor(j * ns.z);
  vec4 y_ = floor(j - 7.0 * x_ );
  vec4 x = x_ *ns.x + ns.yyyy;
  vec4 y = y_ *ns.x + ns.yyyy;
  vec4 h = 1.0 - abs(x) - abs(y);
  vec4 b0 = vec4( x.xy, y.xy );
  vec4 b1 = vec4( x.zw, y.zw );
  vec4 s0 = floor(b0)*2.0 + 1.0;
  vec4 s1 = floor(b1)*2.0 + 1.0;
  vec4 sh = -step(h, vec4(0.0));
  vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy ;
  vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww ;
  vec3 p0 = vec3(a0.xy,h.x);
  vec3 p1 = vec3(a0.zw,h.y);
  vec3 p2 = vec3(a1.xy,h.z);
  vec3 p3 = vec3(a1.zw,h.w);
  vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2, p2), dot(p3,p3)));
  p0 *= norm.x;
  p1 *= norm.y;
  p2 *= norm.z;
  p3 *= norm.w;
  vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
  m = m * m;
  return 42.0 * dot( m*m, vec4( dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3) ) );
}`;

const BLEND_SHADER = `
vec3 blendNormal(vec3 base, vec3 blend) { return blend; }
vec3 blendNormal(vec3 base, vec3 blend, float opacity) { return (blendNormal(base, blend) * opacity + base * (1.0 - opacity)); }
`;

// Helper to convert hex to normalized RGB
function normalizeColor(hexCode: string): [number, number, number] {
  let hex = hexCode.replace('#', '');
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((c) => c + c)
      .join('');
  }
  const int = parseInt(hex, 16);
  return [((int >> 16) & 255) / 255, ((int >> 8) & 255) / 255, (255 & int) / 255];
}

// --- MiniGl Class Definition ---

// 1. Define interfaces for the inner classes so TypeScript knows their shape
interface AttributeClass {
  new (e: any): any;
}
interface UniformClass {
  new (e: any): any;
}
interface MaterialClass {
  new (vertexShaders: string, fragments: string, uniforms?: any): any;
}
interface PlaneGeometryClass {
  new (width: number, height: number, n: number, i: number, orientation: string): any;
}
interface MeshClass {
  new (geometry: any, material: any): any;
}

class MiniGl {
  canvas: HTMLCanvasElement;
  gl: WebGLRenderingContext;
  meshes: any[];
  commonUniforms: any;

  // 2. Explicitly declare the nested classes as properties
  Attribute: AttributeClass;
  Uniform: UniformClass;
  Material: MaterialClass;
  PlaneGeometry: PlaneGeometryClass;
  Mesh: MeshClass;

  constructor(canvas: HTMLCanvasElement, width?: number, height?: number) {
    this.canvas = canvas;
    this.gl = canvas.getContext('webgl', { antialias: true })!;
    this.meshes = [];
    const context = this.gl;

    if (width && height) {
      this.setSize(width, height);
    }

    const _miniGl = this;

    // 3. Define the classes and assign them to the properties

    this.Attribute = class Attribute {
      type: number;
      normalized: boolean;
      buffer: WebGLBuffer;
      target: number;
      values: any;
      size: number;

      constructor(e: any) {
        this.type = context.FLOAT;
        this.normalized = false;
        this.buffer = context.createBuffer()!;
        this.target = context.ARRAY_BUFFER;
        this.values = undefined;
        this.size = 0;
        Object.assign(this, e);
        this.update();
      }
      update() {
        if (this.values !== undefined) {
          context.bindBuffer(this.target, this.buffer);
          context.bufferData(this.target, this.values, context.STATIC_DRAW);
        }
      }
      attach(e: any, t: any) {
        const n = context.getAttribLocation(t, e);
        if (this.target === context.ARRAY_BUFFER) {
          context.enableVertexAttribArray(n);
          context.vertexAttribPointer(n, this.size, this.type, this.normalized, 0, 0);
        }
        return n;
      }
      use(e: any) {
        context.bindBuffer(this.target, this.buffer);
        if (this.target === context.ARRAY_BUFFER) {
          context.enableVertexAttribArray(e);
          context.vertexAttribPointer(e, this.size, this.type, this.normalized, 0, 0);
        }
      }
    };

    this.Uniform = class Uniform {
      type: string;
      value: any;
      transpose: boolean = false;
      excludeFrom?: string;

      constructor(e: any) {
        this.type = 'float';
        Object.assign(this, e);
        this.update();
      }
      update(value?: any) {
        if (this.value !== undefined) {
          // @ts-ignore
          const method =
            {
              float: '1f',
              int: '1i',
              vec2: '2fv',
              vec3: '3fv',
              vec4: '4fv',
              mat4: 'Matrix4fv',
            }[this.type] || '1f';

          if (method.indexOf('Matrix') === 0) {
            // @ts-ignore
            context[`uniform${method}`](value, this.transpose, this.value);
          } else {
            // @ts-ignore
            context[`uniform${method}`](value, this.value);
          }
        }
      }
      // @ts-ignore
      getDeclaration(name: string, type: string, length?: number) {
        if (this.excludeFrom !== type) {
          if (this.type === 'array') {
            return (
              (this.value[0] as any).getDeclaration(name, type, this.value.length) +
              `\nconst int ${name}_length = ${this.value.length};`
            );
          }
          if (this.type === 'struct') {
            let name_no_prefix = name.replace('u_', '');
            name_no_prefix = name_no_prefix.charAt(0).toUpperCase() + name_no_prefix.slice(1);
            return (
              `uniform struct ${name_no_prefix} {\n` +
              Object.entries(this.value)
                .map(([n, u]: any) => u.getDeclaration(n, type).replace(/^uniform/, ''))
                .join('') +
              `\n} ${name}${length! > 0 ? `[${length}]` : ''};`
            );
          }
          return `uniform ${this.type} ${name}${length! > 0 ? `[${length}]` : ''};`;
        }
        return '';
      }
    };

    this.Material = class Material {
      vertexSource: string;
      Source: string;
      vertexShader: any;
      fragmentShader: any;
      program: any;
      uniforms: any;
      uniformInstances: any[];

      constructor(vertexShaders: string, fragments: string, uniforms: any = {}) {
        this.uniforms = uniforms;
        this.uniformInstances = [];

        const prefix = '\n precision highp float;\n';
        this.vertexSource = `\n ${prefix}\n attribute vec4 position;\n attribute vec2 uv;\n attribute vec2 uvNorm;\n ${_miniGl.commonUniforms.projectionMatrix.getDeclaration('projectionMatrix', 'vertex')}\n ${_miniGl.commonUniforms.modelViewMatrix.getDeclaration('modelViewMatrix', 'vertex')}\n ${_miniGl.commonUniforms.resolution.getDeclaration('resolution', 'vertex')}\n ${_miniGl.commonUniforms.aspectRatio.getDeclaration('aspectRatio', 'vertex')}\n ${this.getUniformVariableDeclarations(uniforms, 'vertex')}\n ${vertexShaders}\n`;
        this.Source = `\n ${prefix}\n ${_miniGl.commonUniforms.resolution.getDeclaration('resolution', 'fragment')}\n ${this.getUniformVariableDeclarations(uniforms, 'fragment')}\n ${fragments}\n`;

        this.vertexShader = this.getShaderByType(context.VERTEX_SHADER, this.vertexSource);
        this.fragmentShader = this.getShaderByType(context.FRAGMENT_SHADER, this.Source);
        this.program = context.createProgram();
        context.attachShader(this.program, this.vertexShader);
        context.attachShader(this.program, this.fragmentShader);
        context.linkProgram(this.program);
        context.useProgram(this.program);
        this.attachUniforms(undefined, _miniGl.commonUniforms);
        this.attachUniforms(undefined, this.uniforms);
      }

      getUniformVariableDeclarations(uniforms: any, type: string) {
        return Object.entries(uniforms)
          .map(([uniform, value]: any) => value.getDeclaration(uniform, type))
          .join('\n');
      }
      getShaderByType(type: number, source: string) {
        const shader = context.createShader(type)!;
        context.shaderSource(shader, source);
        context.compileShader(shader);
        if (!context.getShaderParameter(shader, context.COMPILE_STATUS)) {
          console.error(context.getShaderInfoLog(shader));
        }
        return shader;
      }
      attachUniforms(name: string | undefined, uniforms: any) {
        if (name === undefined) {
          Object.entries(uniforms).forEach(([n, u]) => this.attachUniforms(n, u));
        } else if (uniforms.type === 'array') {
          uniforms.value.forEach((u: any, i: number) => this.attachUniforms(`${name}[${i}]`, u));
        } else if (uniforms.type === 'struct') {
          Object.entries(uniforms.value).forEach(([n, u]) => this.attachUniforms(`${name}.${n}`, u));
        } else {
          this.uniformInstances.push({ uniform: uniforms, location: context.getUniformLocation(this.program, name) });
        }
      }
    };

    this.PlaneGeometry = class PlaneGeometry {
      attributes: any;
      xSegCount: number = 0;
      ySegCount: number = 0;
      vertexCount: number = 0;
      quadCount: number = 0;
      width: number = 0;
      height: number = 0;
      orientation: string = 'xz';

      constructor(width: number, height: number, n: number, i: number, orientation: string) {
        context.createBuffer();
        this.attributes = {
          position: new _miniGl.Attribute({ target: context.ARRAY_BUFFER, size: 3 }),
          uv: new _miniGl.Attribute({ target: context.ARRAY_BUFFER, size: 2 }),
          uvNorm: new _miniGl.Attribute({ target: context.ARRAY_BUFFER, size: 2 }),
          index: new _miniGl.Attribute({ target: context.ELEMENT_ARRAY_BUFFER, size: 3, type: context.UNSIGNED_SHORT }),
        };
        this.setTopology(n, i);
        this.setSize(width, height, orientation);
      }
      setTopology(e = 1, t = 1) {
        this.xSegCount = e;
        this.ySegCount = t;
        this.vertexCount = (this.xSegCount + 1) * (this.ySegCount + 1);
        this.quadCount = this.xSegCount * this.ySegCount * 2;
        this.attributes.uv.values = new Float32Array(2 * this.vertexCount);
        this.attributes.uvNorm.values = new Float32Array(2 * this.vertexCount);
        this.attributes.index.values = new Uint16Array(3 * this.quadCount);

        for (let e = 0; e <= this.ySegCount; e++) {
          for (let t = 0; t <= this.xSegCount; t++) {
            const i = e * (this.xSegCount + 1) + t;
            this.attributes.uv.values[2 * i] = t / this.xSegCount;
            this.attributes.uv.values[2 * i + 1] = 1 - e / this.ySegCount;
            this.attributes.uvNorm.values[2 * i] = (t / this.xSegCount) * 2 - 1;
            this.attributes.uvNorm.values[2 * i + 1] = 1 - (e / this.ySegCount) * 2;
            if (t < this.xSegCount && e < this.ySegCount) {
              const s = e * this.xSegCount + t;
              this.attributes.index.values[6 * s] = i;
              this.attributes.index.values[6 * s + 1] = i + 1 + this.xSegCount;
              this.attributes.index.values[6 * s + 2] = i + 1;
              this.attributes.index.values[6 * s + 3] = i + 1;
              this.attributes.index.values[6 * s + 4] = i + 1 + this.xSegCount;
              this.attributes.index.values[6 * s + 5] = i + 2 + this.xSegCount;
            }
          }
        }
        this.attributes.uv.update();
        this.attributes.uvNorm.update();
        this.attributes.index.update();
      }
      setSize(width = 1, height = 1, orientation = 'xz') {
        this.width = width;
        this.height = height;
        this.orientation = orientation;
        if (!(this.attributes.position.values && this.attributes.position.values.length === 3 * this.vertexCount)) {
          this.attributes.position.values = new Float32Array(3 * this.vertexCount);
        }
        const o = width / -2;
        const r = height / -2;
        const segment_width = width / this.xSegCount;
        const segment_height = height / this.ySegCount;
        for (let yIndex = 0; yIndex <= this.ySegCount; yIndex++) {
          const t = r + yIndex * segment_height;
          for (let xIndex = 0; xIndex <= this.xSegCount; xIndex++) {
            const r = o + xIndex * segment_width;
            const l = yIndex * (this.xSegCount + 1) + xIndex;
            this.attributes.position.values[3 * l + 'xyz'.indexOf(orientation[0])] = r;
            this.attributes.position.values[3 * l + 'xyz'.indexOf(orientation[1])] = -t;
          }
        }
        this.attributes.position.update();
      }
    };

    this.Mesh = class Mesh {
      geometry: any;
      material: any;
      wireframe: boolean;
      attributeInstances: any[];

      constructor(geometry: any, material: any) {
        this.geometry = geometry;
        this.material = material;
        this.wireframe = false;
        this.attributeInstances = [];
        Object.entries(this.geometry.attributes).forEach(([e, attribute]: any) => {
          this.attributeInstances.push({ attribute: attribute, location: attribute.attach(e, this.material.program) });
        });
        _miniGl.meshes.push(this);
      }
      draw() {
        context.useProgram(this.material.program);
        this.material.uniformInstances.forEach(({ uniform, location }: any) => uniform.update(location));
        this.attributeInstances.forEach(({ attribute, location }) => attribute.use(location));
        context.drawElements(
          this.wireframe ? context.LINES : context.TRIANGLES,
          this.geometry.attributes.index.values.length,
          context.UNSIGNED_SHORT,
          0
        );
      }
    };

    const a = [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];
    this.commonUniforms = {
      projectionMatrix: new this.Uniform({ type: 'mat4', value: a }),
      modelViewMatrix: new this.Uniform({ type: 'mat4', value: a }),
      resolution: new this.Uniform({ type: 'vec2', value: [1, 1] }),
      aspectRatio: new this.Uniform({ type: 'float', value: 1 }),
    };
  }

  setSize(e = 640, t = 480) {
    this.canvas.width = e;
    this.canvas.height = t;
    this.gl.viewport(0, 0, e, t);
    if (!this.commonUniforms) return;
    this.commonUniforms.resolution.value = [e, t];
    this.commonUniforms.aspectRatio.value = e / t;
  }

  setOrthographicCamera(e = 0, t = 0, n = 0, i = -2e3, s = 2e3) {
    this.commonUniforms.projectionMatrix.value = [
      2 / this.canvas.width,
      0,
      0,
      0,
      0,
      2 / this.canvas.height,
      0,
      0,
      0,
      0,
      2 / (i - s),
      0,
      e,
      t,
      n,
      1,
    ];
  }

  render() {
    this.gl.clearColor(0, 0, 0, 0);
    this.gl.clearDepth(1);
    this.meshes.forEach((e) => e.draw());
  }
}

// --- Main Component ---

const GradientWavesBackground = ({
  colors = ['#a960ee', '#ff333d', '#90e0ff', '#ffcb57'],
  className = 'w-full h-full',
}: GradientBackgroundProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Configuration
    const conf = {
      density: [0.06, 0.16],
      zoom: 1,
      rotation: 0,
      playing: true,
      time: 0,
    };

    // Core state
    let minigl: MiniGl;
    let uniforms: any;
    let plane: InstanceType<MiniGl['PlaneGeometry']> | undefined;

    // Initialize MiniGl
    minigl = new MiniGl(canvas, canvas.offsetWidth, canvas.offsetHeight);

    // Helpers
    const initGradient = (width: number, height: number) => {
      // @ts-ignore
      plane = new minigl.PlaneGeometry(
        width,
        height,
        Math.ceil(width * conf.density[0]),
        Math.ceil(height * conf.density[1]),
        'xz'
      );

      // Prepare uniforms
      // @ts-ignore
      const shaderUniforms = {
        u_time: new minigl.Uniform({ value: 0 }),
        u_shadow_power: new minigl.Uniform({ value: 5 }),
        u_darken_top: new minigl.Uniform({ value: 0 }),
        u_active_colors: new minigl.Uniform({ value: [1, 1, 1, 1], type: 'vec4' }),
        u_global: new minigl.Uniform({
          value: {
            noiseFreq: new minigl.Uniform({ value: [0.00014, 0.00029], type: 'vec2' }),
            noiseSpeed: new minigl.Uniform({ value: 5e-6 }),
          },
          type: 'struct',
        }),
        u_vertDeform: new minigl.Uniform({
          value: {
            incline: new minigl.Uniform({ value: Math.sin(0) / Math.cos(0) }),
            offsetTop: new minigl.Uniform({ value: -0.5 }),
            offsetBottom: new minigl.Uniform({ value: -0.5 }),
            noiseFreq: new minigl.Uniform({ value: [3, 4], type: 'vec2' }),
            noiseAmp: new minigl.Uniform({ value: 320 }),
            noiseSpeed: new minigl.Uniform({ value: 10 }),
            noiseFlow: new minigl.Uniform({ value: 3 }),
            noiseSeed: new minigl.Uniform({ value: 5 }),
          },
          type: 'struct',
          excludeFrom: 'fragment',
        }),
        u_baseColor: new minigl.Uniform({ value: normalizeColor(colors[0]), type: 'vec3', excludeFrom: 'fragment' }),
        u_waveLayers: new minigl.Uniform({ value: [], excludeFrom: 'fragment', type: 'array' }),
      };

      for (let e = 1; e < colors.length; e += 1) {
        shaderUniforms.u_waveLayers.value.push(
          // @ts-ignore
          new minigl.Uniform({
            value: {
              color: new minigl.Uniform({ value: normalizeColor(colors[e]), type: 'vec3' }),
              noiseFreq: new minigl.Uniform({ value: [2 + e / colors.length, 3 + e / colors.length], type: 'vec2' }),
              noiseSpeed: new minigl.Uniform({ value: 11 + 0.3 * e }),
              noiseFlow: new minigl.Uniform({ value: 6.5 + 0.3 * e }),
              noiseSeed: new minigl.Uniform({ value: 5 + 10 * e }),
              noiseFloor: new minigl.Uniform({ value: 0.1 }),
              noiseCeil: new minigl.Uniform({ value: 0.63 + 0.07 * e }),
            },
            type: 'struct',
          })
        );
      }

      // @ts-ignore
      const vertexShader = [NOISE_SHADER, BLEND_SHADER, VERTEX_SHADER].join('\n\n');
      // @ts-ignore
      const material = new minigl.Material(vertexShader, FRAGMENT_SHADER, shaderUniforms);
      // @ts-ignore
      const mesh = new minigl.Mesh(plane, material);

      return { material, mesh, plane };
    };

    // Initial setup
    const { material } = initGradient(canvas.offsetWidth, canvas.offsetHeight);
    uniforms = material.uniforms;

    // Resize Handler
    const handleResize = () => {
      if (!canvasRef.current) return;
      const w = canvasRef.current.offsetWidth;
      const h = canvasRef.current.offsetHeight;
      minigl.setSize(w, h);
      minigl.setOrthographicCamera();
      if (plane) {
        plane.setSize(w, h, 'xz');
      }
      // We'd ideally reconstruct the mesh here if density depends on width,
      // but for simplicity in React we just let the scaling handle it or
      // rely on the initial high vertex count.
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // trigger once

    // Animation Loop
    let last = 0;
    const animate = (timestamp: number) => {
      if (!conf.playing) return;

      if (timestamp - last > 10) {
        // Limit to ~60-100fps if needed, or run as fast as possible
        conf.time += Math.min(timestamp - last, 1000 / 15);
        last = timestamp;
        uniforms.u_time.value = conf.time;
        minigl.render();
      }
      requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);

    // Cleanup
    return () => {
      conf.playing = false;
      window.removeEventListener('resize', handleResize);
    };
  }, [colors]); // Re-run if colors change

  return (
    <div className={`fixed left-0 top-0 -z-10 h-full w-full ${className}`}>
      <canvas ref={canvasRef} className="block h-full w-full" />
    </div>
  );
};

export default GradientWavesBackground;
